import Vue from 'vue';

export const ProjectSites = {
  namespaced: true,

  state: {
    sites: {},
    pagination: null,
    loading: false,
    loadedSites: false,
    loadedForms: false,
    loadedSettings: false,
    loadedDbFields: false,
    loadedFormInstances: false,
    loadedToolFilesBySite: {},
    projectSitesSearch: null,
    saving: false,
    savingSettings: false,
    settings: {},
    canAdd: false,
    canEdit: false,
    savingFormConfiguration: false,
    savingDbFields: false,
    formInstances: [],
    dbFields: [],
    forms: [],
    toolFilesBySite: {},
  },

  getters: {
    getSites: (state) => (uuid) => {
      return state.sites[uuid] ?? [];
    },

    isUserAllowedToCreate: ({ canAdd }) => canAdd,

    isUserAllowedToEdit: ({ canEdit }) => canEdit,

    areSitesLoaded: (state) => (uuid) => {
      if (!state.sites) {
        return false;
      }

      return Object.keys(state.sites).includes(uuid);
    },

    areSitesLoading: (state) => state.loading,

    loadedSites: ({ loadedSites }) => loadedSites,

    getPaginationData:
      ({ pagination }) =>
      (uuid) =>
        pagination?.[uuid] ?? {},

    getFormInstanceId:
      ({ formInstances }) =>
      (formId) => {
        const indexedFormInstances = formInstances.reduce((result, instance) => {
          return { ...result, [instance.form_id]: instance.instance_id };
        }, {});

        return indexedFormInstances[formId] ?? null;
      },

    hasInstances: ({ formInstances }) => formInstances && formInstances.length,

    getVisibleFields: (state) => state.dbFields.filter(({ visible }) => visible),

    getAllDbFields: (state) => state.dbFields,

    toolFiles: (state) => (siteUuid) => state.toolFilesBySite[siteUuid] ?? [],

    loadedToolFiles: (state) => (siteUuid) => !!state.loadedToolFilesBySite[siteUuid],
  },

  mutations: {
    setLoading(state, loading) {
      state.loading = loading;
    },

    updateSites(state, newSite) {
      state.sites = { ...state.sites, ...newSite };
    },

    removeSite(state, removedSite) {
      let uuid = Object.keys(removedSite)[0];
      state.sites[uuid] = state.sites[uuid].filter((site) => site.id !== removedSite[uuid]);
    },

    setUserCanAdd(state, bool) {
      state.canAdd = bool;
    },

    setUserCanEdit(state, bool) {
      state.canEdit = bool;
    },

    setPagination(state, pagination) {
      state.pagination = { ...state.pagination, ...pagination };
    },

    setLoadedSettings(state, bool) {
      state.loadedSettings = bool;
    },

    setSavingSettings(state, bool) {
      state.savingSettings = bool;
    },

    setSettings(state, settings) {
      state.settings = settings;
    },

    setLoadedForms(state, bool) {
      state.loadedForms = bool;
    },

    setLoadedSites(state, bool) {
      state.loadedSites = bool;
    },

    setLoadedFormInstances(state, bool) {
      state.loadedFormInstances = bool;
    },

    clearFormInstances(state) {
      state.formInstances = [];
    },

    setForms(state, forms) {
      state.forms = forms;
    },

    setFormInstances(state, formInstances) {
      state.formInstances = formInstances;
    },

    setProjectSitesSearch(state, search) {
      state.projectSitesSearch = search;
    },

    setLoadedDbFields(state, bool) {
      state.loadedDbFields = bool;
    },

    setDbFields(state, config) {
      state.dbFields = config;
    },

    setSavingDbFields(state, bool) {
      state.savingDbFields = bool;
    },

    setLoadedToolFilesBySite(state, { site, value }) {
      Vue.set(state.loadedToolFilesBySite, site, value);
    },

    setToolFilesBySite(state, { site, value }) {
      Vue.set(state.toolFilesBySite, site, value);
    },

    resetProjectSites(state, projectUuid) {
      Vue.delete(state.sites, projectUuid);
    },

    removeToolFile(state, { site, uuid }) {
      const fileArr = state.toolFilesBySite[site];
      Vue.delete(
        fileArr,
        fileArr.findIndex((f) => f.uuid === uuid)
      );
    },
  },

  actions: {
    async loadSites({ commit }, request) {
      if (!request.project) {
        return;
      }
      commit('setLoading', true);
      commit('setLoadedSites', false);
      let sites = {};
      let pagination = {};
      try {
        const response = await this._vm.$api(
          'project.sites.index',
          {
            ...request,
            version: '1.0',
          },
          {},
          request.cancelToken
        );

        sites = response.data.data;
        pagination = { ...response.data.meta, ...response.data.links };
        commit('setUserCanAdd', response.data.can?.add);
        commit('setUserCanEdit', response.data.can?.edit);
      } catch (err) {
        console.error(err);
      }
      commit('updateSites', { [request.project]: sites });
      commit('setPagination', { [request.project]: pagination });
      commit('setLoading', false);
      commit('setLoadedSites', true);
    },

    async createSite({ commit }, { project, basicForm, metadata_instances }) {
      const { reference, status, member_uuids, site_uuid } = basicForm;

      commit('setLoading', true);

      try {
        await this._vm.$api(
          'project.sites.store',
          {
            version: '1.0',
            project,
          },
          {
            basics: {
              reference,
              status,
              members_uuids: member_uuids,
              site_uuid,
            },
            metadata_instances,
          }
        );
      } catch (err) {
        console.error(err);
      }

      commit('setLoading', false);
    },

    async updateSite({ commit }, { projectUuid, basicForm, siteId, metadata_instances }) {
      commit('setLoading', true);

      const basicFormCopy = { ...basicForm, members_uuids: basicForm.member_uuids };
      delete basicFormCopy.members;
      delete basicFormCopy.member_uuids;

      try {
        await this._vm.$api(
          'project.sites.update',
          {
            version: '1.0',
            project: projectUuid,
            site: siteId,
          },
          { basics: basicFormCopy, metadata_instances }
        );
      } catch (err) {
        console.error(err);
      }

      commit('setLoading', false);
    },

    async deleteSite({ commit }, request) {
      const project = request.project;
      const site = request.projectSite;
      commit('setLoading', true);

      try {
        await this._vm.$api(
          'project.sites.destroy',
          {
            version: '1.0',
            project,
            site,
          },
          { comment: request.comment ?? null }
        );

        commit('removeSite', { [request.project]: site });
      } catch (err) {
        console.error(err);
      }
      commit('setLoading', false);
    },

    async loadSettings({ commit }, { projectUuid }) {
      commit('setLoadedSettings', false);

      try {
        const response = await this._vm.$api('project.site_settings.fetch', {
          project: projectUuid,
          version: '1.0',
        });
        const settings = response?.data?.data ?? {};

        commit('setSettings', settings);
      } catch (err) {
        console.error(err);
      }

      commit('setLoadedSettings', true);
    },

    async saveSettings({ commit }, { projectUuid, settings }) {
      commit('setSavingSettings', true);

      try {
        await this._vm.$api(
          'project.site_settings.save',
          {
            project: projectUuid,
          },
          settings
        );

        commit('setSettings', settings);
      } catch (err) {
        console.error(err);
      }

      commit('setSavingSettings', false);
    },

    async loadForms({ commit }, project) {
      commit('setLoadedForms', false);

      try {
        const response = await this._vm.$api('project.sites.forms', {
          project,
        });

        const formsData = response.data?.data ?? [];

        commit('setForms', formsData);
      } catch (err) {
        console.error(err);
      }
      commit('setLoadedForms', true);
    },

    async loadFormInstances({ commit }, { projectUuid, siteUuid }) {
      if (!projectUuid) {
        return;
      }

      commit('setLoadedFormInstances', false);

      try {
        let response = null;

        if (!siteUuid) {
          response = await this._vm.$api('project.sites.forms.instances', {
            project: projectUuid,
          });
        } else {
          response = await this._vm.$api('project.sites.forms.site_instances', {
            project: projectUuid,
            site: siteUuid,
          });
        }

        const instancesData = response.data?.data ?? [];

        commit('setFormInstances', instancesData);
      } catch (err) {
        console.error(err);
      }

      commit('setLoadedFormInstances', true);
    },

    async getDbFields({ commit }, { projectUuid }) {
      commit('setLoadedDbFields', false);
      commit('setDbFields', []);

      try {
        const response = await this._vm.$api('project.site_settings_fields.fetch', {
          version: '1.0',
          project: projectUuid,
        });

        const fieldsData = response.data?.data ?? [];

        commit('setDbFields', fieldsData);
      } catch (err) {
        console.error(err);
      }

      commit('setLoadedDbFields', true);
    },

    async saveDbFields({ commit }, { projectUuid, fields }) {
      commit('setSavingDbFields', true);

      try {
        await this._vm.$api(
          'project.site_settings_fields.save',
          {
            project: projectUuid,
          },
          { fields }
        );
      } catch (err) {
        console.error(err);
      }

      setTimeout(() => {
        commit('setSavingDbFields', false);
      }, 1000);
    },

    async export(_, { projectUuid, fields, filter }) {
      const response = await this._vm.$api(
        'project.sites.export',
        {
          project: projectUuid,
        },
        {
          fields,
          filter: filter ?? {},
        }
      );

      return response.data?.data?.url ?? '';
    },

    clearFormInstances({ commit }) {
      commit('clearFormInstances');
    },

    setProjectSitesSearch({ commit }, search) {
      commit('setProjectSitesSearch', search);
    },

    resetToolFiles({ commit }, site) {
      commit('setLoadedToolFilesBySite', { site, value: false });
      commit('setToolFilesBySite', { site, value: [] });
    },

    resetSites({ commit }, projectUuid) {
      commit('resetProjectSites', projectUuid);
    },

    removeToolFile({ commit }, { site, uuid }) {
      commit('removeToolFile', { site, uuid });
    },
  },
};
